import React, { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Cpu } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { COMPANY } from "../../data/content";

const HeroSceneLazy = lazy(() =>
  import("../HeroScene").then((m) => ({ default: m.HeroScene }))
);

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export const Hero = () => {
  return (
    <section
      id="hero"
      className="relative hero-gradient grid-lines noise-overlay overflow-hidden"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-16 pb-20 md:pt-24 md:pb-28 lg:grid lg:grid-cols-12 lg:items-center lg:gap-8 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          <motion.div variants={item}>
            <Badge
              variant="outline"
              className="border-primary/30 bg-primary/10 text-primary px-3.5 py-1.5 text-xs font-medium rounded-full"
              data-testid="hero-badge"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              UK Registered IT Consultancy · Company No. {COMPANY.companyNumber}
            </Badge>
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mt-6 leading-[1.08]"
          >
            We Build, Secure &amp;{" "}
            <span className="text-gradient-cyan">Scale</span> Your Digital
            Business
          </motion.h1>

          <motion.p
            variants={item}
            className="text-base md:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed"
          >
            Websites, mobile apps, cyber security, Amazon &amp; e-commerce growth,
            and custom systems for any business. Describe what you need — FCY IT
            designs, builds and launches it.
          </motion.p>

          {/* Keep conversion controls visible independently of entrance animations. */}
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button
              onClick={() => scrollTo("#contact")}
              className="hero-project-cta rounded-xl h-12 px-7 text-base font-medium"
              data-testid="hero-primary-cta-button"
            >
              Start Your Project <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              onClick={() => scrollTo("#services")}
              variant="outline"
              className="rounded-xl h-12 px-7 text-base border-white/10 bg-white/5 text-foreground hover:bg-white/10 hover:text-foreground"
              data-testid="hero-secondary-cta-button"
            >
              Explore Services
            </Button>
          </div>

          <motion.div
            variants={item}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-sm text-muted-foreground"
          >
            <span className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-primary" /> Websites &amp; Mobile Apps
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" /> Cyber Security
            </span>
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Amazon &amp; E-Commerce
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
          className="lg:col-span-5 mt-12 lg:mt-0 h-[320px] sm:h-[400px] lg:h-[480px] relative"
          data-testid="hero-3d-scene"
        >
          <Suspense
            fallback={
              <div className="w-full h-full flex items-center justify-center">
                <div className="h-40 w-40 rounded-full bg-primary/10 animate-pulse" />
              </div>
            }
          >
            <HeroSceneLazy />
          </Suspense>
        </motion.div>
      </div>
    </section>
  );
};
